"use client";

import Script from "next/script";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { SOL_MINT } from "@/lib/revive-create/sol-constants";
import { dispatchWalletOpenConnectMenu } from "@/utils/walletConnectAttention";
import {
  getJupiterBranding,
  getJupiterFormPropsExtras,
} from "@/lib/token-dex/jupiter-plugin-env";
import {
  JUPITER_PLUGIN_SCRIPT_INTEGRITY,
  JUPITER_PLUGIN_SCRIPT_SRC,
} from "@/lib/token-dex/jupiterPluginSri";
import { DexScreenerChart } from "@/components/token-dex/DexScreenerChart";
import { TokenDexLiveActivitiesTable } from "@/components/token-dex/TokenDexLiveActivitiesTable";

function jupiterContainerId(instanceId) {
  return `jupiter-token-dex-plugin-${instanceId}`;
}

function closeJupiterPlugin() {
  if (typeof window === "undefined") return;
  try {
    const J = window.Jupiter;
    if (J && typeof J.close === "function") J.close();
  } catch {
    /* noop */
  }
}

/** Jupiter `containerStyles`: inner chrome only; outer border is on the host `div`. */
const JUPITER_SHELL_STYLES = {
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "inset 0 1px 0 0 rgba(129, 140, 248, 0.12)",
  backgroundColor: "rgba(3, 7, 18, 0.92)",
};

/**
 * DexScreener embed + Jupiter integrated swap (same wiring as token hub).
 * @param {string} mint — output mint (swap into this token).
 * @param {string} [instanceId="hub"] — unique DOM id suffix if multiple panels can exist in one session.
 */
export function TokenDexChartSwapPanel({ mint, instanceId = "hub", eagerActivityLoad = true }) {
  const wallet = useWallet();
  const isWalletConnected = Boolean(wallet?.connected);
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  const [pluginReady, setPluginReady] = useState(false);
  const containerId = jupiterContainerId(instanceId);

  useEffect(() => {
    const trimmed = mint?.trim();
    if (!trimmed) return;
    void fetch(`/api/token-dex/dex-pair?mint=${encodeURIComponent(trimmed)}`, {
      cache: "no-store",
    });
    void fetch(`/api/token-dex/pool-activity?mint=${encodeURIComponent(trimmed)}`, {
      cache: "no-store",
    });
  }, [mint]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const href = JUPITER_PLUGIN_SCRIPT_SRC;
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "script";
    link.href = href;
    link.integrity = JUPITER_PLUGIN_SCRIPT_INTEGRITY;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  }, []);

  const jupiterReferralFormProps = useMemo(() => getJupiterFormPropsExtras(), []);
  const jupiterBranding = useMemo(() => getJupiterBranding(), []);

  const onRequestConnectWallet = useCallback(() => {
    dispatchWalletOpenConnectMenu();
  }, []);

  const pushSwapAnalytics = useCallback(
    (kind, detail) => {
      if (typeof window === "undefined") return;
      if (process.env.NODE_ENV === "development") {
        if (kind === "success") console.info("[jupiter-plugin] swap success", detail?.txid, mint);
        else console.warn("[jupiter-plugin] swap error", detail?.error, mint);
      }
      const dl = window.dataLayer;
      if (!Array.isArray(dl)) return;
      if (kind === "success") {
        dl.push({
          event: "jupiter_swap_success",
          txid: detail?.txid,
          token_mint: mint,
        });
      } else {
        dl.push({
          event: "jupiter_swap_error",
          token_mint: mint,
          message: detail?.error?.message ?? String(detail?.error ?? ""),
        });
      }
    },
    [mint],
  );

  useEffect(() => {
    if (!pluginReady || typeof window === "undefined" || !window.Jupiter?.init) return undefined;

    closeJupiterPlugin();

    const formProps = {
      initialInputMint: SOL_MINT,
      initialOutputMint: mint,
      fixedMint: mint,
      ...jupiterReferralFormProps,
    };

    const passthrough = walletRef.current;

    window.Jupiter.init({
      displayMode: "integrated",
      integratedTargetId: containerId,
      enableWalletPassthrough: true,
      passthroughWalletContextState: passthrough,
      onRequestConnectWallet,
      formProps,
      branding: jupiterBranding,
      containerStyles: JUPITER_SHELL_STYLES,
      onSuccess: ({ txid }) => {
        pushSwapAnalytics("success", { txid });
      },
      onSwapError: ({ error }) => {
        pushSwapAnalytics("error", { error });
      },
    });

    queueMicrotask(() => {
      const J = window.Jupiter;
      const w = walletRef.current;
      if (J?.syncProps && w) J.syncProps({ passthroughWalletContextState: w });
      // Remote plugin bundle may read the live ref; re-apply after (re)init.
      if (J?.enableWalletPassthrough) {
        J.onRequestConnectWallet = onRequestConnectWallet;
      }
    });

    return () => closeJupiterPlugin();
  }, [
    mint,
    pluginReady,
    containerId,
    jupiterReferralFormProps,
    jupiterBranding,
    onRequestConnectWallet,
    pushSwapAnalytics,
  ]);

  useEffect(() => {
    if (!pluginReady || typeof window === "undefined" || !window.Jupiter?.syncProps) return;
    const J = window.Jupiter;
    J.syncProps({ passthroughWalletContextState: wallet });
    if (J.enableWalletPassthrough) {
      J.onRequestConnectWallet = onRequestConnectWallet;
    }
  }, [pluginReady, wallet, onRequestConnectWallet]);

  /** Purple rotating ring + indigo frame — always on (hub + community). */
  const swapHostFrameClass =
    "border border-indigo-400/35 shadow-[0_0_30px_-12px_rgba(99,102,241,0.5)]";

  return (
    <>
      <Script
        src={JUPITER_PLUGIN_SCRIPT_SRC}
        integrity={JUPITER_PLUGIN_SCRIPT_INTEGRITY}
        crossOrigin="anonymous"
        strategy="afterInteractive"
        onReady={() => setPluginReady(true)}
      />

      <div className="flex w-full min-w-0 max-w-full flex-col gap-6 overflow-x-hidden lg:flex-row lg:items-stretch">
        {/*
          Mobile/tablet: taller chart slot; lg unchanged. min-w-0/max-w-full keeps flex within viewport.
        */}
        <div className="relative h-[min(60dvh,34rem)] min-h-[320px] w-full min-w-0 max-w-full flex-1 overflow-hidden rounded-xl bg-black/40 sm:h-[560px] sm:min-h-[400px] md:h-[620px] lg:h-[628px]">
          <DexScreenerChart address={mint} />
        </div>
        <div className="flex w-full min-w-0 max-w-full shrink-0 flex-col gap-3 justify-center lg:block lg:w-[380px] lg:justify-start">
          {!isWalletConnected ? (
            <button
              type="button"
              onClick={onRequestConnectWallet}
              className="inline-flex w-full items-center justify-center rounded-lg border border-emerald-500/40 bg-emerald-600/10 px-3 py-2 text-sm font-medium text-emerald-200 transition-colors hover:bg-emerald-600/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 lg:hidden"
            >
              Connect mobile wallet to swap
            </button>
          ) : null}
          <div className="relative w-full max-w-[min(100%,420px)] overflow-hidden rounded-xl lg:max-w-none">
            <span className="idx-jupiter-swap-connect-spin" aria-hidden />
            <div
              id={containerId}
              className={`relative z-[1] m-[1px] h-[min(72dvh,34rem)] w-[calc(100%-2px)] overflow-hidden rounded-[11px] bg-gray-950 sm:h-[calc(36rem-2px)] lg:h-[598px] ${swapHostFrameClass}`}
            />
          </div>
        </div>
      </div>

      <TokenDexLiveActivitiesTable mint={mint} eagerLoad={eagerActivityLoad} />
    </>
  );
}
